import { useLocale } from '@solinkcorp/locale';
import { Input, Label, Separator } from '@solinkcorp/solink-ui';
import type { UseFormReturn } from 'react-hook-form';
import type { DayPartFormData } from '../../../hooks/useDayPartForm';
import { useDayPartsLocale } from '../../../hooks/useDayPartLocale';
import { DayPartDayRow } from './DayPartDayRow';
import { ScheduleConflictHelperText } from './ScheduleConflictHelperText';

import { DAY_INDICES } from '../../../day-part/constants';
import { type DayIndex } from '../../../day-part/utils';

interface DayPartFormProps {
  form: UseFormReturn<DayPartFormData>;
  conflictLayoutNames?: string[] | null;
}

export const DayPartForm = ({ form, conflictLayoutNames = null }: DayPartFormProps) => {
  const locale = useLocale();
  const formLocale = locale.serviceDisplayMonitor.dayPartDialog;

  const dayLabels = useDayPartsLocale();
  const days = form.watch('days');

  const activeDays: DayIndex[] = [];
  const inactiveDays: DayIndex[] = [];
  for (const idx of DAY_INDICES) {
    if (days[idx]?.active) {
      activeDays.push(idx);
    } else {
      inactiveDays.push(idx);
    }
  }

  const handleCopyToAll = () => {
    if (activeDays.length === 0) return;
    const source = days[activeDays[0]];
    if (!source) return;
    for (const idx of activeDays) {
      form.setValue(`days.${idx}.active`, true);
      form.setValue(`days.${idx}.start`, source.start);
      form.setValue(`days.${idx}.end`, source.end);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-foreground-primary label-sm">
          {formLocale.dialogTitle} <span className="text-destructive">*</span>
        </Label>
        <Input
          {...form.register('label')}
          className="text-text-primary placeholder:text-foreground-secondary"
          placeholder={`${locale.serviceDisplayMonitor.dayPartDialog.addDayPartName}`}
          aria-invalid={!!form.formState.errors.label}
        />
        {form.formState.errors.label && (
          <p className="pl-2 pt-1 text-destructive caption">
            {form.formState.errors.label.message}
          </p>
        )}
      </div>

      {activeDays.length > 0 && (
        <div className="space-y-1">
          <Label className="text-muted-foreground caption">{formLocale.activeDays}</Label>
          {activeDays.map((idx, i) => (
            <DayPartDayRow
              key={idx}
              dayIndex={idx}
              dayLabel={dayLabels[Number(idx)]}
              control={form.control}
              showCopyToAll={i === 0}
              onCopyToAll={handleCopyToAll}
            />
          ))}
        </div>
      )}
      {activeDays.length < 7 && activeDays.length > 0 && <Separator />}
      {inactiveDays.length > 0 && (
        <div className="space-y-1">
          <Label className="text-muted-foreground caption">{formLocale.inactiveDays}</Label>
          {inactiveDays.map((idx) => (
            <DayPartDayRow
              key={idx}
              dayIndex={idx}
              dayLabel={dayLabels[Number(idx)]}
              control={form.control}
            />
          ))}
        </div>
      )}
      <ScheduleConflictHelperText layoutNames={conflictLayoutNames ?? []} />
    </div>
  );
};
